import { RecordCollection } from '../../app/record-collection.js';
import {
  Button,
  ChoiceChips,
  Combobox,
  CurrencyInput,
  DatePicker,
  FormErrorSummary,
  Modal,
  StatusPill,
  ToggleChips,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
  IconButton,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tag, PencilLine, Ban } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { SalesTermsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  getApiError,
  salesApi,
  type SalesPromotion,
  type SalesPromotionStatus,
  type SalesPromotionTargetKind,
} from '../../lib/api.js';
import type { SalesUnit } from './sales-packages.js';
import { PROMOTION_STATUS, discountText, termText, unitsText } from './sales-terms.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

const SEARCH_DELAY_MS = 250;
const KIND_LABEL: Record<SalesPromotionTargetKind, string> = {
  SERVICE: 'Serviço',
  PRODUCT: 'Produto',
  PACKAGE: 'Pacote',
};

/// Arquivado é situação só do pacote: na lista de promoções, cai na de vigentes.
function promotionTerm(term: SalesTermsView['term']): SalesPromotionStatus {
  return term === 'ARCHIVED' ? 'ACTIVE' : term;
}

export function SalesPromotionsList({
  view,
  onView,
  units,
  today,
  canManage,
}: {
  view: SalesTermsView;
  onView: ViewSetter<SalesTermsView>;
  units: readonly SalesUnit[];
  today: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [editing, setEditing] = useState<{ promotion: SalesPromotion | null }>();
  const [ending, setEnding] = useState<SalesPromotion>();
  const term = promotionTerm(view.term);
  const search = view.search.trim();
  const page = useQuery({
    queryKey: [
      'company',
      'sales',
      'promotions',
      term,
      search,
      view.limit,
      view.offset,
      view.sort,
      view.direction,
    ],
    queryFn: () =>
      salesApi.promotions({
        ...(search ? { search } : {}),
        status: term,
        ...(view.sort ? { sort: view.sort, direction: view.direction ?? 'asc' } : {}),
        limit: view.limit,
        offset: view.offset,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  function promotionActions(promotion: SalesPromotion) {
    return (
      <div className="flex shrink-0 gap-1">
        {canManage && promotion.status !== 'ENDED' ? (
          <IconButton
            label={`Editar promoção de ${promotion.targetDisplayName}`}
            icon={<PencilLine />}
            onClick={() => setEditing({ promotion })}
          />
        ) : null}
        {canManage && promotion.status === 'ACTIVE' ? (
          <IconButton
            label={`Encerrar hoje a promoção de ${promotion.targetDisplayName}`}
            icon={<Ban />}
            tone="danger"
            onClick={() => setEnding(promotion)}
          />
        ) : null}
      </div>
    );
  }
  return (
    <section aria-labelledby="sales-promotions-title">
      <PageHeader
        trail="Catálogo / Promoções"
        id="sales-promotions-title"
        title="Promoções"
        description="Outro preço para um serviço, produto ou pacote, nas unidades e datas escolhidas."
        actions={
          canManage ? (
            <CreateButton onClick={() => setEditing({ promotion: null })}>
              Nova promoção
            </CreateButton>
          ) : null
        }
      />
      <FilterBar
        searchId="sales-promotions-search"
        searchLabel="Buscar promoção"
        searchPlaceholder="Serviço, produto ou pacote"
        search={view.search}
        onSearchChange={(next) => onView({ search: next, offset: 0 }, { replace: true })}
        chips={
          term === 'ACTIVE'
            ? []
            : [
                {
                  key: 'term',
                  label: PROMOTION_STATUS[term].plural,
                  onRemove: () => onView({ term: 'ACTIVE', offset: 0 }),
                },
              ]
        }
        onClear={() => onView({ search: '', term: 'ACTIVE', offset: 0 })}
        count={`${total} ${total === 1 ? 'promoção' : 'promoções'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'promoção' : 'promoções'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit, offset: 0 })}
      >
        <ChoiceChips
          label="Situação das promoções"
          value={term}
          onChange={(next) => onView({ term: next as SalesPromotionStatus, offset: 0 })}
          options={(Object.keys(PROMOTION_STATUS) as SalesPromotionStatus[]).map((key) => ({
            value: key,
            label: `${PROMOTION_STATUS[key].plural} · ${String(page.data?.counts[key] ?? 0)}`,
          }))}
        />
      </FilterBar>
      {page.isLoading ? (
        <LoadingCard label="Carregando promoções" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={Tag}
          title={`Nenhuma promoção ${PROMOTION_STATUS[term].label.toLowerCase()}`}
          description="Promoção dá outro preço a um serviço, produto ou pacote nas unidades e datas escolhidas."
        />
      ) : (
        <RecordCollection
          label="Promoções"
          records={page.data?.promotions ?? []}
          recordKey={(promotion) => promotion.id}
          sorting={{
            sort: view.sort ?? 'date',
            direction: view.direction ?? 'desc',
            columns: ['name', 'date', 'price'],
            onToggle: (key) => {
              if (key === 'name' || key === 'date' || key === 'price')
                onView({
                  sort: key,
                  direction:
                    key === (view.sort ?? 'date') && (view.direction ?? 'desc') === 'asc'
                      ? 'desc'
                      : 'asc',
                  offset: 0,
                });
            },
          }}
          columns={[
            {
              key: 'name',
              label: 'Promoção',
              render: (promotion) => <strong>{promotion.targetDisplayName}</strong>,
            },
            {
              key: 'kind',
              label: 'Tipo',
              render: (promotion) => KIND_LABEL[promotion.targetKind],
            },
            {
              key: 'units',
              label: 'Unidades',
              render: (promotion) => (
                <span className="text-xs text-content-muted">
                  {unitsText(promotion.businessUnitIds, units)}
                </span>
              ),
            },
            {
              key: 'date',
              label: 'Vigência',
              render: (promotion) => (
                <span className="text-xs text-content-muted">
                  {termText(promotion.validFrom, promotion.validUntil)}
                </span>
              ),
            },
            {
              key: 'price',
              label: 'Preço',
              render: (promotion) => (
                <div className="grid gap-1">
                  <strong className="whitespace-nowrap tabular-nums">
                    {formatCurrency(promotion.priceCents, promotion.currency)}
                  </strong>
                  {promotion.normalPriceCents ? (
                    <span className="text-xs text-content-muted">
                      <s>{formatCurrency(promotion.normalPriceCents, promotion.currency)}</s> ·{' '}
                      <span>{discountText(promotion.normalPriceCents, promotion.priceCents)}</span>
                    </span>
                  ) : null}
                </div>
              ),
            },
            {
              key: 'status',
              label: 'Situação',
              render: (promotion) => (
                <StatusPill tone={PROMOTION_STATUS[promotion.status].tone}>
                  {PROMOTION_STATUS[promotion.status].label}
                </StatusPill>
              ),
            },
          ]}
          actions={promotionActions}
          card={(promotion) => {
            const status = PROMOTION_STATUS[promotion.status];
            return (
              <article
                key={promotion.id}
                className="flex flex-col gap-3 rounded-2xl border border-surface-border bg-surface-card p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <h3 className="font-semibold">{promotion.targetDisplayName}</h3>
                  <p className="mt-1 text-xs text-content-muted">
                    {KIND_LABEL[promotion.targetKind]} ·{' '}
                    {unitsText(promotion.businessUnitIds, units)} ·{' '}
                    {termText(promotion.validFrom, promotion.validUntil)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                  <div className="grid gap-1 sm:justify-items-end sm:text-right">
                    <span>
                      {promotion.normalPriceCents ? (
                        <s className="mr-2 text-xs text-content-muted">
                          {formatCurrency(promotion.normalPriceCents, promotion.currency)}
                        </s>
                      ) : null}
                      <strong className="tabular-nums">
                        {formatCurrency(promotion.priceCents, promotion.currency)}
                      </strong>
                    </span>
                    {promotion.normalPriceCents ? (
                      <span className="text-xs text-content-muted">
                        <span>
                          {discountText(promotion.normalPriceCents, promotion.priceCents)}
                        </span>
                      </span>
                    ) : null}
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                  </div>
                  {promotionActions(promotion)}
                </div>
              </article>
            );
          }}
        />
      )}
      <Pagination
        offset={view.offset}
        limit={view.limit}
        total={total}
        onOffsetChange={(offset) => onView({ offset })}
        itemLabel={(count) => (count === 1 ? 'promoção' : 'promoções')}
      />
      {editing ? (
        <PromotionDialog
          key={editing.promotion?.id ?? 'new'}
          promotion={editing.promotion}
          units={units}
          today={today}
          onClose={() => setEditing(undefined)}
          onSaved={async (saved) => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
            setEditing(undefined);
            onView({ term: saved.status, offset: 0 });
            snackbar.success(`Promoção de ${saved.targetDisplayName} salva.`);
          }}
        />
      ) : null}
      {ending ? (
        <EndPromotionDialog
          promotion={ending}
          today={today}
          onClose={() => setEnding(undefined)}
          onEnded={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
            setEnding(undefined);
            snackbar.success('A promoção termina hoje.');
          }}
        />
      ) : null}
    </section>
  );
}

interface Target {
  kind: SalesPromotionTargetKind;
  referenceId: string;
  displayName: string;
  normalPriceCents: number;
  /// Só no pacote: as unidades onde ele é vendido, as únicas que a promoção pode usar.
  businessUnitIds?: readonly string[] | undefined;
}

function PromotionDialog({
  promotion,
  units,
  today,
  onClose,
  onSaved,
}: {
  promotion: SalesPromotion | null;
  units: readonly SalesUnit[];
  today: string;
  onClose: () => void;
  onSaved: (saved: SalesPromotion) => Promise<void>;
}) {
  const [target, setTarget] = useState<Target | undefined>(
    promotion
      ? {
          kind: promotion.targetKind,
          referenceId: promotion.referenceId,
          displayName: promotion.targetDisplayName,
          normalPriceCents: promotion.normalPriceCents ?? 0,
        }
      : undefined,
  );
  const [price, setPrice] = useState(promotion?.priceCents ?? 0);
  const [validFrom, setValidFrom] = useState<string | null>(promotion?.validFrom ?? today);
  const [validUntil, setValidUntil] = useState<string | null>(promotion?.validUntil ?? null);
  const [unitIds, setUnitIds] = useState<string[]>(
    () => promotion?.businessUnitIds ?? units.map((unit) => unit.id),
  );
  const [submitted, setSubmitted] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);
  const allowed = units.filter(
    (unit) => !target?.businessUnitIds || target.businessUnitIds.includes(unit.id),
  );
  const errors = {
    target: target ? undefined : 'Escolha o serviço, produto ou pacote.',
    price:
      price <= 0
        ? 'Informe o preço na promoção.'
        : target?.normalPriceCents && price >= target.normalPriceCents
          ? `O preço na promoção precisa ficar abaixo do normal, ${formatCurrency(target.normalPriceCents, 'BRL')}.`
          : undefined,
    validFrom: validFrom ? undefined : 'Informe quando a promoção começa.',
    validUntil:
      validFrom && validUntil && validUntil < validFrom
        ? 'A data de fim vem antes do início.'
        : undefined,
    units: unitIds.length === 0 ? 'Escolha pelo menos uma unidade.' : undefined,
  };
  const show = (message: string | undefined) => (submitted ? message : undefined);

  async function save() {
    setSubmitted(true);
    setFailure(undefined);
    if (Object.values(errors).some(Boolean)) return;
    const changes = {
      priceCents: price,
      validFrom: validFrom!,
      validUntil,
      businessUnitIds: unitIds,
    };
    setBusy(true);
    try {
      await onSaved(
        promotion
          ? await salesApi.updatePromotion(promotion.id, changes)
          : await salesApi.createPromotion({
              ...changes,
              targetKind: target!.kind,
              referenceId: target!.referenceId,
            }),
      );
    } catch (reason) {
      setFailure(getApiError(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={promotion ? 'Editar promoção' : 'Nova promoção'}
      description="Por item e unidade, só uma promoção vale por vez."
      footer={
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <FormErrorSummary form="sales-promotion-form" className="sm:mr-auto sm:flex-1" />
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="sales-promotion-form" disabled={busy}>
            {promotion ? 'Salvar promoção' : 'Criar promoção'}
          </Button>
        </div>
      }
    >
      <form
        id="sales-promotion-form"
        noValidate
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {target ? (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-surface-border p-3">
            <span>
              <strong>{target.displayName}</strong>{' '}
              <span className="text-xs text-content-muted">{KIND_LABEL[target.kind]}</span>
            </span>
            {promotion ? null : (
              <Button size="sm" variant="ghost" onClick={() => setTarget(undefined)}>
                Trocar
              </Button>
            )}
          </div>
        ) : (
          <TargetSearch
            error={show(errors.target)}
            onPick={(picked) => {
              setTarget(picked);
              if (picked.businessUnitIds) {
                const inside = unitIds.filter((id) => picked.businessUnitIds!.includes(id));
                setUnitIds(inside.length > 0 ? inside : [...picked.businessUnitIds]);
              }
            }}
          />
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <span className="mb-1.5 block text-xs font-semibold text-content/70">Preço normal</span>
            <p className="py-2 text-content-muted">
              {target?.normalPriceCents ? formatCurrency(target.normalPriceCents, 'BRL') : '—'}
            </p>
          </div>
          <CurrencyInput
            label="Preço na promoção"
            currency="BRL"
            value={price}
            error={show(errors.price)}
            onValueChange={(value) => setPrice(value ?? 0)}
          />
          <DatePicker
            label="Começa em"
            value={validFrom}
            onValueChange={setValidFrom}
            today={today}
            error={show(errors.validFrom)}
          />
          <DatePicker
            label="Termina em (opcional)"
            value={validUntil}
            onValueChange={setValidUntil}
            today={today}
            error={show(errors.validUntil)}
            hint={validUntil ? undefined : '∞ Sem data de fim'}
          />
        </div>
        <div
          role="group"
          aria-label="Unidades"
          aria-invalid={Boolean(show(errors.units))}
          tabIndex={-1}
        >
          <ToggleChips
            label="Unidades da promoção"
            itemNoun="unidades"
            options={allowed.map((unit) => ({ value: unit.id, label: unit.displayName }))}
            selected={unitIds}
            onChange={setUnitIds}
          />
          {show(errors.units) ? (
            <p role="alert" className="mt-1 text-xs text-danger">
              {errors.units}
            </p>
          ) : null}
        </div>
        {target ? (
          <p className="text-xs text-content-muted">
            {target.kind === 'PACKAGE'
              ? 'A promoção do pacote só pode usar as unidades onde ele é vendido. O valor no saldo será repartido do preço da promoção.'
              : 'Vale para a venda avulsa. Dentro de pacotes, o item continua com o valor do pacote.'}
          </p>
        ) : null}
        {failure ? <RequestAlert message={failure} /> : null}
      </form>
    </Modal>
  );
}

/// Serviços e produtos do catálogo da venda e os pacotes que ainda se vendem.
function TargetSearch({
  error,
  onPick,
}: {
  error: string | undefined;
  onPick: (target: Target) => void;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const catalog = useQuery({
    queryKey: ['company', 'sales', 'catalog', query],
    queryFn: () => salesApi.catalog(query || undefined),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const packages = useQuery({
    queryKey: ['company', 'sales', 'promotion-packages', query],
    queryFn: async () => {
      const filters = { ...(query ? { search: query } : {}), limit: 20, offset: 0 };
      const [active, scheduled] = await Promise.all([
        salesApi.packages({ ...filters, status: 'ACTIVE' }),
        salesApi.packages({ ...filters, status: 'SCHEDULED' }),
      ]);
      return [...active.packages, ...scheduled.packages];
    },
    placeholderData: keepPreviousData,
    retry: false,
  });
  const targets: Target[] = [
    ...[...(catalog.data?.services ?? []), ...(catalog.data?.products ?? [])].map((item) => ({
      kind: item.kind,
      referenceId: item.id,
      displayName: item.displayName,
      normalPriceCents: item.basePriceCents,
    })),
    ...(packages.data ?? []).map((found) => ({
      kind: 'PACKAGE' as const,
      referenceId: found.id,
      displayName: found.displayName,
      normalPriceCents: found.priceCents,
      businessUnitIds: found.businessUnitIds,
    })),
  ];
  const key = (target: Target) => `${target.kind}:${target.referenceId}`;
  return (
    <Combobox
      label="Serviço, produto ou pacote"
      placeholder="Buscar pelo nome"
      selectedLabel=""
      clearable={false}
      error={error}
      options={targets.map((target) => ({
        value: key(target),
        label: target.displayName,
        description: `${KIND_LABEL[target.kind]} · ${formatCurrency(target.normalPriceCents, 'BRL')}`,
      }))}
      onSelect={(choice) => {
        const found = targets.find((target) => key(target) === (choice as { value: string }).value);
        if (found) onPick(found);
      }}
      onQueryChange={setText}
      loading={catalog.isFetching || packages.isFetching}
      emptyLabel="Nada encontrado"
    />
  );
}

function EndPromotionDialog({
  promotion,
  today,
  onClose,
  onEnded,
}: {
  promotion: SalesPromotion;
  today: string;
  onClose: () => void;
  onEnded: () => Promise<void>;
}) {
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);
  const tomorrow = new Date(`${today}T12:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Encerrar promoção"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Voltar
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              void (async () => {
                setBusy(true);
                try {
                  await salesApi.endPromotion(promotion.id);
                  await onEnded();
                } catch (reason) {
                  setFailure(getApiError(reason));
                } finally {
                  setBusy(false);
                }
              })()
            }
          >
            Encerrar
          </Button>
        </div>
      }
    >
      <p>
        {promotion.targetDisplayName} volta ao preço normal a partir de amanhã,{' '}
        {formatCivilDate(tomorrow.toISOString().slice(0, 10))}. Vendas já feitas não mudam.
      </p>
      {failure ? <RequestAlert message={failure} /> : null}
    </Modal>
  );
}
